import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { useTranslation } from './hooks/useTranslation';
import LanguageSwitcher from './components/LanguageSwitcher';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import AdminLayout from './components/AdminLayout';
import UserManagement from './components/UserManagement';
import VehicleManagement from './components/Admin/VehicleManagement';
import MifareCardManagement from './components/MifareCardManagement';
import OBUManagement from './components/OBUManagement';
import CheckBalance from './components/CheckBalance';
import QRTopup from './components/QRTopup';
import ResetPassword from './components/ResetPassword';
import TransactionHistory from './components/TransactionHistory';
import EventsTable from './components/EventsTable';
import Reports from './components/Reports';
import RevenueAnalysis from './components/RevenueAnalysis';
import Settings from './components/Settings';
import WIMManagement from './components/WIMManagement';
import WIMFineTiers from './components/WIMFineTiers';
import WIMTruckTypes from './components/WIMTruckTypes';
import WIMPayment from './components/WIMPayment';
import ManualPayment from './components/ManualPayment';
import PermissionGuard from './components/PermissionGuard';
import API from './api';
import { Toaster, toast } from 'react-hot-toast';

// Helper function to get first accessible route based on user permissions
function getFirstAccessibleRoute(permissions) {
  if (!permissions || !Array.isArray(permissions)) {
    console.warn('⚠️  No permissions array provided, using reset-password as fallback');
    return '/admin/reset-password'; // Default fallback - accessible to all
  }

  // Map of routes to required permissions (matching AdminLayout menu items)
  const routePermissions = [
    { path: '/admin/dashboard', module: 'dashboard', action: 'view' },
    { path: '/admin/users', module: 'users', action: 'view' },
    { path: '/admin/vehicles', module: 'vehicles', action: 'view' },
    { path: '/admin/mifare', module: 'mifare', action: 'view' },
    { path: '/admin/obu-management', module: 'obu', action: 'view' },
    { path: '/admin/check-balance', module: 'mifare', action: 'view' }, // Admin path
    { path: '/admin/check-balance', module: 'customer', action: 'check_balance' }, // Customer path
    { path: '/admin/topup', module: 'mifare', action: 'topup' }, // Admin path
    { path: '/admin/topup', module: 'customer', action: 'qr_topup' }, // Customer path
    { path: '/admin/manual-payment', module: 'manual_payment', action: 'process' },
    { path: '/admin/wim', module: 'wim', action: 'view' },
    { path: '/admin/wim-fine-tiers', module: 'settings', action: 'edit' },
    { path: '/admin/wim-truck-types', module: 'settings', action: 'edit' },
    { path: '/admin/wim-payment', module: 'wim', action: 'process_payment' },
    { path: '/admin/transactions', module: 'transactions', action: 'view' },
    { path: '/admin/reports', module: 'reports', action: 'view' },
    { path: '/admin/analysis', module: 'reports', action: 'view' },
    { path: '/admin/settings', module: 'settings', action: 'view' },
  ];

  // Group routes by path, then check all permission variants for each path
  const pathGroups = {};
  for (const route of routePermissions) {
    if (!pathGroups[route.path]) pathGroups[route.path] = [];
    pathGroups[route.path].push({ module: route.module, action: route.action });
  }

  // Walk through routes in order (using original order to preserve priority)
  const seenPaths = new Set();
  for (const route of routePermissions) {
    if (seenPaths.has(route.path)) continue; // already evaluated this path
    seenPaths.add(route.path);

    // Check all permission variants for this path (OR logic)
    const variants = pathGroups[route.path];
    const hasAccess = variants.some(v =>
      permissions.some(p => p.module === v.module && p.action === v.action)
    );

    if (hasAccess) {
      console.log(`✅ User has access to: ${route.path}`);
      return route.path;
    }
  }

  console.warn('⚠️  No accessible routes found, using reset-password as fallback');
  return '/admin/reset-password'; // Fallback - accessible to all authenticated users
}

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getMessage } = useTranslation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      API.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          // Store user data with permissions in localStorage
          localStorage.setItem('user', JSON.stringify(res.data));

          setIsAuthenticated(true);

          // Navigate to first accessible route based on permissions
          if (location.pathname === '/login') {
            const firstRoute = getFirstAccessibleRoute(res.data.permissions);
            console.log(`🚪 Redirecting user to: ${firstRoute}`);
            navigate(firstRoute, { replace: true });
          }

          // Only try to get balance if user has a mifare card
          if (res.data.card_uid) {
            return API.get(`/mifare/balance/${res.data.card_uid}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Admin or users without cards don't need balance
            return Promise.resolve();
          }
        })
        .then(res => {
          if (res && res.data) {
            // Balance logic removed as state is unused
          }
        })
        .catch((error) => {
          // Only reset auth if it's actually an auth error
          if (error.response?.status === 401) {
            setIsAuthenticated(false);
            localStorage.removeItem('user');
          } else {
            // For other errors (like missing card), just continue
            console.warn('Could not load balance:', error.message);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    const token = localStorage.getItem('token');
    API.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        // Store user data with permissions in localStorage
        localStorage.setItem('user', JSON.stringify(res.data));


        // Navigate to first accessible route based on permissions
        const firstRoute = getFirstAccessibleRoute(res.data.permissions);
        console.log(`🚪 Redirecting user to: ${firstRoute}`);
        navigate(firstRoute, { replace: true });

        // Only try to get balance if user has a mifare card
        if (res.data.card_uid) {
          return API.get(`/mifare/balance/${res.data.card_uid}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          // Admin or users without cards don't need balance
          toast.success(`✅ ${getMessage('login_success')}`);
          return Promise.resolve();
        }
      })
      .then(res => {
        if (res && res.data) {
          toast.success(`✅ ${getMessage('login_success')}`);
        }
      })
      .catch((error) => {
        console.error('Login error:', error);
        toast.error(`❌ ${getMessage('error_occurred')}`);
      });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    toast(`👋 ${getMessage('logout_success')}`);
    // No need to call message in Thai as it will be handled by the translation system
  };

  return (
    <div className={darkMode ? 'dark' : ''}>
      <Toaster position="top-right" />

      {/* Only show header for non-admin routes */}
      {!location.pathname.startsWith('/admin') && (
        <header className="bg-white dark:bg-gray-800 text-black dark:text-white shadow p-4 flex justify-between items-center">
          <div>
            {/* Empty space or minimal branding */}
          </div>
          <div className="flex gap-2 items-center">
            <LanguageSwitcher compact={true} />
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="px-3 py-1 bg-gray-300 dark:bg-gray-700 rounded"
            >
              {darkMode ? '☀️ Light' : '🌙 Dark'}
            </button>
            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600"
              >
                {getMessage('auth.logout')}
              </button>
            )}
          </div>
        </header>
      )}

      <main className={`${location.pathname.startsWith('/admin') ? '' : 'p-6'} bg-gray-100 dark:bg-gray-900 min-h-screen text-black dark:text-white`}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/register" element={<Register onRegister={handleLogin} />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Redirect root to first accessible route based on user permissions */}
          <Route path="/" element={
            isAuthenticated ? (
              <Navigate to={getFirstAccessibleRoute(JSON.parse(localStorage.getItem('user') || '{}').permissions || [])} replace />
            ) : (
              <Navigate to="/login" />
            )
          } />

          {/* Admin routes - accessible to all authenticated users with permission filtering */}
          <Route
            path="/admin/*"
            element={
              isAuthenticated ? (
                <AdminLayout onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          >
            <Route index element={<Navigate to={getFirstAccessibleRoute(JSON.parse(localStorage.getItem('user') || '{}').permissions || [])} replace />} />
            <Route path="dashboard" element={
              <PermissionGuard module="dashboard" action="view">
                <Dashboard />
              </PermissionGuard>
            } />
            <Route path="users" element={
              <PermissionGuard module="users" action="view">
                <UserManagement />
              </PermissionGuard>
            } />
            <Route path="vehicles" element={
              <PermissionGuard module="vehicles" action="view">
                <VehicleManagement />
              </PermissionGuard>
            } />
            <Route path="mifare" element={
              <PermissionGuard module="mifare" action="view">
                <MifareCardManagement />
              </PermissionGuard>
            } />
            <Route path="obu-management" element={
              <PermissionGuard module="obu" action="view">
                <OBUManagement />
              </PermissionGuard>
            } />
            <Route path="check-balance" element={
              <PermissionGuard permissions={['mifare.view', 'obu.view', 'customer.check_balance']}>
                <CheckBalance />
              </PermissionGuard>
            } />
            <Route path="topup" element={
              <PermissionGuard permissions={['mifare.topup', 'customer.qr_topup']}>
                <QRTopup />
              </PermissionGuard>
            } />
            <Route path="manual-payment" element={
              <PermissionGuard module="manual_payment" action="process">
                <ManualPayment />
              </PermissionGuard>
            } />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="transactions" element={
              <PermissionGuard module="transactions" action="view">
                <TransactionHistory />
              </PermissionGuard>
            } />
            <Route path="events" element={
              <PermissionGuard module="reports" action="view">
                <EventsTable />
              </PermissionGuard>
            } />
            <Route path="analysis" element={
              <PermissionGuard module="reports" action="view">
                <RevenueAnalysis />
              </PermissionGuard>
            } />
            <Route path="reports" element={
              <PermissionGuard module="reports" action="view">
                <Reports />
              </PermissionGuard>
            } />
            <Route path="settings" element={
              <PermissionGuard module="settings" action="view">
                <Settings />
              </PermissionGuard>
            } />
            <Route path="wim" element={
              <PermissionGuard module="wim" action="view">
                <WIMManagement />
              </PermissionGuard>
            } />
            <Route path="wim-fine-tiers" element={
              <PermissionGuard module="settings" action="edit">
                <WIMFineTiers />
              </PermissionGuard>
            } />
            <Route path="wim-truck-types" element={
              <PermissionGuard module="settings" action="edit">
                <WIMTruckTypes />
              </PermissionGuard>
            } />
            <Route path="wim-payment" element={
              <PermissionGuard module="wim" action="process_payment">
                <WIMPayment />
              </PermissionGuard>
            } />
          </Route>
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AppContent />
      </Router>
    </LanguageProvider>
  );
}

export default App;
